import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Users, 
  Activity, 
  DollarSign, 
  Clock, 
  User, 
  FileText, 
  Heart, 
  Bell,
  Search,
  Menu,
  X,
  ChevronRight,
  TrendingUp,
  MapPin,
  Phone,
  Mail,
  Settings,
  LogOut,
  Sun,
  Moon,
  ChevronDown,
  ChevronLeft,
  AlertTriangle,
  Building,
  Stethoscope,
  Package,
  UserCheck,
  BarChart3,
  PieChart,
  Target,
  Shield,
  Zap,
  Filter,
  Download,
  Plus,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Bed,
  Thermometer,
  Pill,
  CreditCard,
  TrendingDown
} from 'lucide-react';
import { LineChart, Line, Pie, AreaChart, Area, BarChart, Bar, PieChart as RechartsPieChart, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const AdvancedClinicDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 2000);
  };

  // Sample data for charts
  const revenueData = [
    { month: 'Jan', revenue: 45000, expenses: 32000, profit: 13000 },
    { month: 'Feb', revenue: 52000, expenses: 35000, profit: 17000 },
    { month: 'Mar', revenue: 48000, expenses: 33000, profit: 15000 },
    { month: 'Apr', revenue: 61000, expenses: 38000, profit: 23000 },
    { month: 'May', revenue: 55000, expenses: 36000, profit: 19000 },
    { month: 'Jun', revenue: 67000, expenses: 41000, profit: 26000 },
  ];

  const patientsByDepartment = [
    { name: 'Cardiology', value: 234, color: '#3b82f6' },
    { name: 'Orthopedics', value: 189, color: '#10b981' },
    { name: 'Pediatrics', value: 156, color: '#f59e0b' },
    { name: 'Emergency', value: 298, color: '#ef4444' },
    { name: 'General', value: 445, color: '#8b5cf6' },
  ];

  const satisfactionData = [
    { period: 'Week 1', rating: 4.2 },
    { period: 'Week 2', rating: 4.5 },
    { period: 'Week 3', rating: 4.1 },
    { period: 'Week 4', rating: 4.7 },
  ];

  const occupancyData = [
    { time: '6AM', doctors: 3, rooms: 5 },
    { time: '9AM', doctors: 8, rooms: 12 },
    { time: '12PM', doctors: 12, rooms: 18 },
    { time: '3PM', doctors: 10, rooms: 15 },
    { time: '6PM', doctors: 6, rooms: 8 },
    { time: '9PM', doctors: 4, rooms: 6 },
  ];

  const stats = [
    { 
      title: 'Total Patients Today', 
      value: '342', 
      change: '+15%', 
      changeValue: '+44',
      icon: Users, 
      color: 'from-blue-500 to-blue-600',
      trend: 'up',
      description: 'vs yesterday'
    },
    { 
      title: 'Appointments', 
      value: '89', 
      change: '+8%', 
      changeValue: '+7',
      icon: Calendar, 
      color: 'from-green-500 to-green-600',
      trend: 'up',
      description: 'scheduled today'
    },
    { 
      title: 'Revenue Today', 
      value: '$12,847', 
      change: '+23%', 
      changeValue: '+$2,341',
      icon: DollarSign, 
      color: 'from-purple-500 to-purple-600',
      trend: 'up',
      description: 'vs yesterday'
    },
    { 
      title: 'Critical Alerts', 
      value: '5', 
      change: '-2', 
      changeValue: '-2',
      icon: AlertTriangle, 
      color: 'from-red-500 to-red-600',
      trend: 'down',
      description: 'active alerts'
    }
  ];

  const criticalAlerts = [
    { id: 1, type: 'Inventory', message: 'Low stock: Surgical masks (12 remaining)', severity: 'high', time: '5 min ago' },
    { id: 2, type: 'Equipment', message: 'MRI Machine #2 requires maintenance', severity: 'medium', time: '15 min ago' },
    { id: 3, type: 'Staff', message: 'Dr. Johnson overtime limit reached', severity: 'low', time: '1 hour ago' },
    { id: 4, type: 'Patient', message: 'Emergency bed capacity at 90%', severity: 'high', time: '2 hours ago' },
  ];

  const quickActions = [
    { icon: Users, title: 'Add Patient', description: 'Register new patient', color: 'from-blue-500 to-blue-600' },
    { icon: Calendar, title: 'Schedule', description: 'Book appointment', color: 'from-green-500 to-green-600' },
    { icon: FileText, title: 'Reports', description: 'Generate reports', color: 'from-purple-500 to-purple-600' },
    { icon: Package, title: 'Inventory', description: 'Manage stock', color: 'from-orange-500 to-orange-600' },
    { icon: UserCheck, title: 'Staff Check', description: 'View attendance', color: 'from-teal-500 to-teal-600' },
    { icon: Shield, title: 'Security', description: 'Access control', color: 'from-red-500 to-red-600' },
  ];

  const navigationItems = [
    { id: 'dashboard', icon: Activity, label: 'Dashboard', badge: null },
    { id: 'patients', icon: Users, label: 'Patients', badge: '2,847' },
    { id: 'appointments', icon: Calendar, label: 'Appointments', badge: '89' },
    { id: 'staff', icon: UserCheck, label: 'Staff', badge: '24' },
    { id: 'records', icon: FileText, label: 'Records', badge: null },
    { id: 'billing', icon: CreditCard, label: 'Billing', badge: '$45k' },
    { id: 'inventory', icon: Package, label: 'Inventory', badge: '5' },
    { id: 'reports', icon: BarChart3, label: 'Reports', badge: null },
    { id: 'settings', icon: Settings, label: 'Settings', badge: null },
  ];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'dashboard':
        return renderDashboardContent();
      case 'patients':
        return renderPatientsContent();
      case 'appointments':
        return renderAppointmentsContent();
      case 'staff':
        return renderStaffContent();
      case 'records':
        return renderRecordsContent();
      case 'billing':
        return renderBillingContent();
      case 'inventory':
        return renderInventoryContent();
      case 'reports':
        return renderReportsContent();
      case 'settings':
        return renderSettingsContent();
      default:
        return renderDashboardContent();
    }
  };

  const renderDashboardContent = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm font-medium mb-1`}>{stat.title}</p>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-3xl font-bold mb-2`}>{stat.value}</p>
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                    stat.trend === 'up' 
                      ? 'bg-green-100 text-green-700' 
                      : stat.trend === 'down' 
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {stat.trend === 'up' ? <ArrowUp className="w-3 h-3" /> : stat.trend === 'down' ? <ArrowDown className="w-3 h-3" /> : null}
                    <span>{stat.change}</span>
                  </div>
                  <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-xs`}>{stat.description}</span>
                </div>
              </div>
              <div className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                <stat.icon className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Revenue Overview</h3>
            <div className="flex space-x-2">
              <button className={`px-3 py-1 rounded-lg text-xs font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                6M
              </button>
              <button className="px-3 py-1 rounded-lg text-xs font-medium bg-blue-500 text-white">
                1Y
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="month" stroke={darkMode ? '#9ca3af' : '#6b7280'} />
              <YAxis stroke={darkMode ? '#9ca3af' : '#6b7280'} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                  border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  color: darkMode ? '#ffffff' : '#000000'
                }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="url(#colorRevenue)" strokeWidth={3} />
              <Area type="monotone" dataKey="profit" stroke="#10b981" fill="url(#colorProfit)" strokeWidth={2} />
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Patients by Department */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
          <h3 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold mb-6`}>Patients by Department</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={patientsByDepartment}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({name, value}) => `${name}: ${value}`}
              >
                {patientsByDepartment.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                  border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  color: darkMode ? '#ffffff' : '#000000'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Occupancy and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clinic Occupancy */}
        <div className={`lg:col-span-2 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
          <h3 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold mb-6`}>Clinic Occupancy Status</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={occupancyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="time" stroke={darkMode ? '#9ca3af' : '#6b7280'} />
              <YAxis stroke={darkMode ? '#9ca3af' : '#6b7280'} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                  border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  color: darkMode ? '#ffffff' : '#000000'
                }}
              />
              <Bar dataKey="doctors" fill="#3b82f6" name="Doctors" radius={[4, 4, 0, 0]} />
              <Bar dataKey="rooms" fill="#10b981" name="Rooms Used" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Critical Alerts */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Critical Alerts</h3>
            <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-medium">
              {criticalAlerts.length}
            </span>
          </div>
          <div className="space-y-4">
            {criticalAlerts.map((alert) => (
              <div key={alert.id} className={`p-4 rounded-xl border-l-4 ${
                alert.severity === 'high' 
                  ? 'border-red-500 bg-red-50' 
                  : alert.severity === 'medium' 
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-blue-500 bg-blue-50'
              } ${darkMode ? 'bg-opacity-10' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        alert.severity === 'high' 
                          ? 'bg-red-100 text-red-700' 
                          : alert.severity === 'medium' 
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {alert.type}
                      </span>
                    </div>
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-900'} text-sm font-medium mb-1`}>
                      {alert.message}
                    </p>
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-xs`}>
                      {alert.time}
                    </p>
                  </div>
                  <button className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} p-1`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h3 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold mb-6`}>Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quickActions.map((action, index) => (
            <button key={index} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} p-4 rounded-xl border-2 border-dashed ${darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'} transition-all duration-200 group`}>
              <div className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform duration-200`}>
                <action.icon className="w-6 h-6 text-white" />
              </div>
              <h4 className={`${darkMode ? 'text-white' : 'text-gray-900'} font-semibold text-sm mb-1`}>{action.title}</h4>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-xs`}>{action.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPatientsContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>Patient Management</h2>
          <div className="flex space-x-3">
            <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Add Patient</span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded-xl p-4`}>
            <div className="flex items-center space-x-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>2,847</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Total Patients</p>
              </div>
            </div>
          </div>
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-4`}>
            <div className="flex items-center space-x-3">
              <UserCheck className="w-8 h-8 text-green-500" />
              <div>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>342</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Active Today</p>
              </div>
            </div>
          </div>
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-purple-50'} rounded-xl p-4`}>
            <div className="flex items-center space-x-3">
              <Activity className="w-8 h-8 text-purple-500" />
              <div>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>89</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>New This Week</p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <th className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} px-4 py-3 text-left font-semibold`}>Patient</th>
                <th className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} px-4 py-3 text-left font-semibold`}>Age</th>
                <th className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} px-4 py-3 text-left font-semibold`}>Department</th>
                <th className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} px-4 py-3 text-left font-semibold`}>Status</th>
                <th className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} px-4 py-3 text-left font-semibold`}>Last Visit</th>
                <th className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} px-4 py-3 text-left font-semibold`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[
                { name: 'John Doe', age: 45, dept: 'Cardiology', status: 'Active', lastVisit: '2024-01-15' },
                { name: 'Jane Smith', age: 32, dept: 'Pediatrics', status: 'Scheduled', lastVisit: '2024-01-10' },
                { name: 'Mike Johnson', age: 67, dept: 'Orthopedics', status: 'Active', lastVisit: '2024-01-12' },
              ].map((patient, index) => (
                <tr key={index} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                  <td className={`${darkMode ? 'text-white' : 'text-gray-900'} px-4 py-3 font-medium`}>{patient.name}</td>
                  <td className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} px-4 py-3`}>{patient.age}</td>
                  <td className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} px-4 py-3`}>{patient.dept}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      patient.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {patient.status}
                    </span>
                  </td>
                  <td className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} px-4 py-3`}>{patient.lastVisit}</td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button className="text-blue-500 hover:text-blue-700">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-green-500 hover:text-green-700">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAppointmentsContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold mb-6`}>Appointment Management</h2>
        <div className="text-center py-20">
          <Calendar className={`w-16 h-16 ${darkMode ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>Appointment management system coming soon...</p>
        </div>
      </div>
    </div>
  );

  const renderStaffContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold mb-6`}>Staff Management</h2>
        <div className="text-center py-20">
          <UserCheck className={`w-16 h-16 ${darkMode ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>Staff management system coming soon...</p>
        </div>
      </div>
    </div>
  );

  const renderRecordsContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold mb-6`}>Medical Records</h2>
        <div className="text-center py-20">
          <FileText className={`w-16 h-16 ${darkMode ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>Medical records system coming soon...</p>
        </div>
      </div>
    </div>
  );

  const renderBillingContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold mb-6`}>Billing & Finance</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-6`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm font-medium`}>Total Revenue</p>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-3xl font-bold mt-2`}>$45,230</p>
                <p className="text-green-600 text-sm font-medium mt-1">+18% vs last month</p>
              </div>
              <DollarSign className="w-12 h-12 text-green-500" />
            </div>
          </div>
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded-xl p-6`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm font-medium`}>Outstanding</p>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-3xl font-bold mt-2`}>$12,847</p>
                <p className="text-blue-600 text-sm font-medium mt-1">15 pending invoices</p>
              </div>
              <Clock className="w-12 h-12 text-blue-500" />
            </div>
          </div>
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-purple-50'} rounded-xl p-6`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm font-medium`}>Collections</p>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-3xl font-bold mt-2`}>94.2%</p>
                <p className="text-purple-600 text-sm font-medium mt-1">Collection rate</p>
              </div>
              <Target className="w-12 h-12 text-purple-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInventoryContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold mb-6`}>Inventory Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-red-50'} rounded-xl p-4`}>
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>5</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Low Stock</p>
              </div>
            </div>
          </div>
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded-xl p-4`}>
            <div className="flex items-center space-x-3">
              <Package className="w-8 h-8 text-blue-500" />
              <div>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>847</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Total Items</p>
              </div>
            </div>
          </div>
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-4`}>
            <div className="flex items-center space-x-3">
              <Pill className="w-8 h-8 text-green-500" />
              <div>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>234</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Medicines</p>
              </div>
            </div>
          </div>
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-purple-50'} rounded-xl p-4`}>
            <div className="flex items-center space-x-3">
              <Stethoscope className="w-8 h-8 text-purple-500" />
              <div>
                <p className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>89</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Equipment</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderReportsContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold mb-6`}>Reports & Analytics</h2>
        <div className="text-center py-20">
          <BarChart3 className={`w-16 h-16 ${darkMode ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>Advanced reporting system coming soon...</p>
        </div>
      </div>
    </div>
  );

  const renderSettingsContent = () => (
    <div className="space-y-6">
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border p-6`}>
        <h2 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-2xl font-bold mb-6`}>System Settings</h2>
        <div className="space-y-6">
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6`}>
            <h3 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold mb-4`}>Appearance</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} font-medium`}>Dark Mode</p>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Toggle dark/light theme</p>
              </div>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  darkMode ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    darkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && !sidebarCollapsed && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full ${sidebarCollapsed ? 'w-20' : 'w-72'} ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-2xl z-50 transform transition-all duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 border-r`}>
        
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className={`flex items-center space-x-3 ${sidebarCollapsed ? 'justify-center' : ''}`}>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Heart className="w-6 h-6 text-white" />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>MediCare Pro</h1>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Advanced System</p>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`hidden lg:flex p-2 ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'} rounded-lg transition-colors`}
              >
                <ChevronLeft className={`w-5 h-5 transform transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
              </button>
              <button 
                onClick={() => setSidebarOpen(false)}
                className={`lg:hidden p-2 ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'} rounded-lg transition-colors`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                if (window.innerWidth < 1024) setSidebarOpen(false);
              }}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} p-3 rounded-xl text-left transition-all duration-200 group ${
                activeTab === item.id 
                  ? `${darkMode ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 border-blue-200'} shadow-lg` 
                  : `${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`
              }`}
            >
              <div className="flex items-center space-x-3">
                <item.icon className={`w-5 h-5 ${activeTab === item.id ? 'text-current' : ''}`} />
                {!sidebarCollapsed && (
                  <span className="font-medium">{item.label}</span>
                )}
              </div>
              {!sidebarCollapsed && item.badge && (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  activeTab === item.id 
                    ? 'bg-white text-blue-600' 
                    : `${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`
                }`}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} rounded-xl transition-colors duration-200`}>
            <LogOut className="w-5 h-5" />
            {!sidebarCollapsed && (
              <span className="font-medium">Sign Out</span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        {/* Header */}
        <header className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b sticky top-0 z-40 backdrop-blur-lg bg-opacity-90`}>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSidebarOpen(true)}
                  className={`lg:hidden p-2 ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'} rounded-lg transition-colors`}
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {navigationItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
                  </h2>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Welcome back, Dr. Anderson
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className={`hidden md:flex items-center space-x-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <Clock className="w-4 h-4" />
                  <span>{currentTime.toLocaleTimeString()}</span>
                </div>
                
                <div className="relative">
                  <Search className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input 
                    type="text" 
                    placeholder="Search patients..."
                    className={`pl-10 pr-4 py-2 border rounded-xl transition-all duration-200 ${
                      darkMode 
                        ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:bg-gray-600 focus:border-blue-500' 
                        : 'border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20`}
                  />
                </div>

                <button 
                  onClick={() => setDarkMode(!darkMode)}
                  className={`p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                >
                  {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                <button 
                  onClick={handleRefresh}
                  className={`p-2 rounded-xl transition-all ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                >
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                </button>

                <button className={`relative p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
                  <Bell className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                </button>

                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-sm font-semibold">DA</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="p-6">
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
};

export default AdvancedClinicDashboard;